import React, { useEffect, useRef, useState } from "react";
import { MENUEE_LOGO_FALLBACK } from "../config/portal";
import { formatearDireccionResumida } from "../utils/direccionDisplay";
import { aplicarIconoDocumento } from "../utils/documentBranding";
import { lineasHorarioEncabezado } from "../utils/horarioDisplay";

function BloqueHorarioPanel({ etiqueta, texto }) {
  const lineas = lineasHorarioEncabezado(texto);
  if (lineas.length === 0) {
    return null;
  }

  return (
    <div className="encabezado-carta__panel-horario-bloque">
      <div className="encabezado-carta__panel-horario-titulo">{etiqueta}</div>
      {lineas.map((linea, indice) => (
        <div key={`${etiqueta}-${indice}`} className="encabezado-carta__panel-horario-linea">
          {linea}
        </div>
      ))}
    </div>
  );
}

function DesplegableHorarios({ horarioAtencion, horarioReparto }) {
  const [abierto, setAbierto] = useState(false);
  const contenedorRef = useRef(null);
  const hayAtencion = lineasHorarioEncabezado(horarioAtencion).length > 0;
  const hayReparto = lineasHorarioEncabezado(horarioReparto).length > 0;

  useEffect(() => {
    if (!abierto) {
      return undefined;
    }

    function cerrarSiAfuera(evento) {
      if (!contenedorRef.current?.contains(evento.target)) {
        setAbierto(false);
      }
    }

    function cerrarConEscape(evento) {
      if (evento.key === "Escape") {
        setAbierto(false);
      }
    }

    document.addEventListener("mousedown", cerrarSiAfuera);
    document.addEventListener("keydown", cerrarConEscape);
    return () => {
      document.removeEventListener("mousedown", cerrarSiAfuera);
      document.removeEventListener("keydown", cerrarConEscape);
    };
  }, [abierto]);

  useEffect(() => {
    setAbierto(false);
  }, [horarioAtencion, horarioReparto]);

  if (!hayAtencion && !hayReparto) {
    return null;
  }

  return (
    <div className="encabezado-carta__horarios" ref={contenedorRef}>
      <button
        type="button"
        className="encabezado-carta__btn-info"
        aria-expanded={abierto}
        aria-haspopup="true"
        aria-label="Ver horarios de atención y repartos"
        onClick={() => setAbierto((actual) => !actual)}
      >
        <i
          className={`bi ${abierto ? "bi-info-circle-fill" : "bi-info-circle"}`}
          aria-hidden="true"
        />
      </button>
      {abierto && (
        <div className="encabezado-carta__panel-horario" role="dialog" aria-label="Horarios de atención y repartos">
          <BloqueHorarioPanel etiqueta="Atención" texto={horarioAtencion} />
          <BloqueHorarioPanel etiqueta="Repartos" texto={horarioReparto} />
        </div>
      )}
    </div>
  );
}

export default function EncabezadoCartaMinimalista({
  onLocalizarTienda,
  modoEntrega,
  setModoEntrega,
  sucursalSeleccionada,
  inicializandoSucursal,
  localNombre,
}) {
  const botonesBloqueados = !sucursalSeleccionada || inicializandoSucursal;
  const sucursal = sucursalSeleccionada;
  const mostrarLocalizar = typeof onLocalizarTienda === "function";
  const nombreNegocio = String(localNombre || "").trim() || "Menuee";
  const logoUrl = sucursal?.logo_url || MENUEE_LOGO_FALLBACK;
  const [logoSrc, setLogoSrc] = useState(logoUrl);

  useEffect(() => {
    setLogoSrc(logoUrl);
  }, [logoUrl]);

  useEffect(() => {
    aplicarIconoDocumento(logoSrc);
    return () => aplicarIconoDocumento(MENUEE_LOGO_FALLBACK);
  }, [logoSrc]);

  return (
    <header
      className="encabezado-carta d-flex flex-wrap justify-content-between align-items-center px-4 py-3 border-bottom"
      style={{
        backgroundColor: "#fff",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <div className="encabezado-carta__marca d-flex align-items-center gap-3 mb-3 mb-md-0 flex-grow-1">
        <img
          src={logoSrc}
          alt={nombreNegocio}
          width={48}
          height={48}
          decoding="async"
          onError={() => {
            if (logoSrc !== MENUEE_LOGO_FALLBACK) {
              setLogoSrc(MENUEE_LOGO_FALLBACK);
            }
          }}
          style={{
            height: 48,
            width: 48,
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
        <span className="fw-semibold" style={{ letterSpacing: 2 }}>
          {nombreNegocio}
        </span>
        {mostrarLocalizar && (
          <button
            type="button"
            className="btn btn-outline-dark rounded-pill fw-semibold ms-md-2 btn-localizar-tienda"
            onClick={onLocalizarTienda}
            disabled={inicializandoSucursal}
            aria-label="Localizar tienda"
          >
            <i className="bi bi-geo-alt-fill btn-localizar-tienda__icon" aria-hidden="true" />
            <span className="btn-localizar-tienda__text btn-localizar-tienda__text--short">
              Localizar
            </span>
            <span className="btn-localizar-tienda__text btn-localizar-tienda__text--full">
              Localizar Tienda
            </span>
          </button>
        )}
      </div>

      <div className="encabezado-carta__modos d-flex gap-2 mb-3 mb-md-0 justify-content-center flex-grow-1 order-3 order-md-0">
        <button
          type="button"
          className={`btn rounded-pill px-4 fw-semibold ${
            modoEntrega === "retiro" ? "btn-dark" : "btn-outline-dark"
          }`}
          disabled={botonesBloqueados}
          onClick={() => setModoEntrega("retiro")}
        >
          Retiro
        </button>

        <button
          type="button"
          className={`btn rounded-pill px-4 fw-semibold ${
            modoEntrega === "domicilio" ? "btn-dark" : "btn-outline-dark"
          }`}
          disabled={botonesBloqueados}
          onClick={() => setModoEntrega("domicilio")}
        >
          A domicilio
        </button>
      </div>

      <div className="encabezado-carta__info d-none d-md-flex flex-column text-end ms-md-4 flex-grow-1">
        {!sucursalSeleccionada && !inicializandoSucursal && (
          <span className="text-muted">
            {mostrarLocalizar ? "Selecciona una sucursal" : "Local no disponible"}
          </span>
        )}

        {inicializandoSucursal && !sucursal && (
          <span className="text-muted">Cargando sucursal...</span>
        )}

        {sucursal && (
          <>
            <div className="encabezado-carta__nombre-fila">
              <span className="fw-semibold text-dark">{sucursal.nombre}</span>
              <DesplegableHorarios
                horarioAtencion={sucursal.horario_atencion}
                horarioReparto={sucursal.horario_reparto}
              />
            </div>
            {sucursal.direccion && (
              <span className="text-muted encabezado-carta__direccion">
                {formatearDireccionResumida(sucursal.direccion)}
              </span>
            )}
          </>
        )}
      </div>
    </header>
  );
}
